import Box from "@mui/material/Box";
import { memo, useMemo } from "react";
import Fade from "@mui/material/Fade";
import Stack from "@mui/material/Stack";
import MainCard from "./Layouts/MainCard";
import { Theme } from "../data/style/Theme";
import LoadingDice from "./Display/LoadingDice";
import { useAuth } from "../context/AuthContext";
import useScreenSize from "../context/ScreenContext";
import ExerciseWeight from "./Display/ExerciseWeight";
import GenWorkoutError from "./Display/GenWorkoutError";
import { WORKOUT_TYPES_DATA } from "../data/assets/arrays";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import {
  CenteredRow,
  AllCenteredColumn,
  MainTitle,
  MainText,
  SpacedRow,
} from "../data/style/Style";

const FT_CONVERT = 3.2808399;
const MILE_CONVERT = 0.000621371192;

const getExerciseKey = (item, index) =>
  `${item.exercise}-${item.reps}-${item.step || 0}-${index}`;

const getRunDistanceLabel = (distanceSetting, altReps) => {
  if (distanceSetting === 0) {
    return `${altReps} m`;
  }

  if (altReps > 1000) {
    return `${(altReps * MILE_CONVERT).toFixed(2)} mi`;
  }

  return `${Math.floor(altReps * FT_CONVERT)} ft`;
};

const getStepRunDistanceLabel = (distanceSetting, item) => {
  if (distanceSetting === 0) {
    return `${item.alt_reps + (item.alt_reps / item.reps) * item.step} m`;
  }

  return `${Math.floor((item.alt_reps + item.alt_step) * FT_CONVERT)} ft`;
};

const GenWodDisplay = ({ wod, loading, error }) => {
  const { profile, settings } = useAuth();
  const { size, mobileDevice } = useScreenSize();
  let [width] = size;

  const isSmallScreen = width < 850;
  const isCompact = width < 700;

  const displayState = useMemo(() => {
    const exercises = wod.exercises ?? [];
    const isFemaleProfile = profile.gender === 1;

    let headerText = null;
    if (wod.type_id === 4) {
      headerText =
        wod.timecap === 0
          ? `${wod.rounds} rounds: ${wod.every} minutes on / ${wod.rest} ${
              wod.rest < 2 ? "minute" : "minutes"
            } off`
          : `Every ${wod.every} minutes for ${wod.timecap} minutes`;
    }

    const baseExercises = exercises.map((item, index) => ({
      key: getExerciseKey(item, index),
      index,
      item,
      displayReps:
        item.reps === 5000
          ? "max"
          : isFemaleProfile
            ? item.female_reps
            : item.reps,
      showMinuteLabel: wod.type_id === 3,
      runLabel:
        item.exercise === "run" && item.reps !== 5000
          ? getRunDistanceLabel(settings.distance, item.alt_reps)
          : null,
    }));

    const stepExercises =
      wod.type_id === 5
        ? exercises.map((item, index) => ({
            key: `step-${getExerciseKey(item, index)}`,
            index,
            item,
            displayReps: item.reps + item.step,
            runLabel:
              item.exercise === "run"
                ? getStepRunDistanceLabel(settings.distance, item)
                : null,
          }))
        : [];

    return {
      baseExercises,
      stepExercises,
      headerText,
      timecapValue: `${isFemaleProfile ? wod.female_timecap : wod.timecap} minutes`,
      projectedScoreValue: `${isFemaleProfile ? wod.female_projected_score : wod.projected_score} reps`,
    };
  }, [
    profile.gender,
    wod.every,
    wod.exercises,
    wod.female_projected_score,
    wod.female_timecap,
    wod.projected_score,
    wod.rest,
    wod.rounds,
    wod.timecap,
    wod.type_id,
    settings.distance,
  ]);

  if (loading) {
    return (
      <MainCard>
        <AllCenteredColumn gap={1} sx={{ marginTop: "20%" }}>
          <LoadingDice />
          <MainTitle fontSize={mobileDevice ? "0.8rem" : "1rem"} mt={2}>
            Generating Workout
          </MainTitle>
          <MainText
            fontSize={"0.7rem"}
            sx={{ color: Theme.palette.muted + " !important" }}
          >
            Crafting the perfect workout for you...
          </MainText>
        </AllCenteredColumn>
      </MainCard>
    );
  }

  if (error) {
    return (
      <MainCard>
        <GenWorkoutError />
      </MainCard>
    );
  }
  const workout_type = WORKOUT_TYPES_DATA.find(
    (item) => item.id === wod.type_id,
  );
  return (
    <MainCard>
      <Stack gap={1}>
        <Stack>
          <Stack direction={"row"} alignItems={"center"} gap={1}>
            <AllCenteredColumn sx={{ width: 30, height: 30, borderRadius: 4 }}>
              <span style={{ color: "#fff" }}>{workout_type.icon}</span>
            </AllCenteredColumn>
            <MainTitle fontSize={mobileDevice ? "1.2rem" : "1.5rem"}>
              {workout_type.name}
            </MainTitle>
          </Stack>
          <MainText
            fontSize={"0.8rem"}
            sx={{ color: Theme.palette.muted + " !important" }}
          >
            {wod.type_id !== 4 ? (
              <>
                ⏱ Timecap:{" "}
                <span style={{ color: Theme.palette.orange }}>
                  {displayState.timecapValue}
                </span>
              </>
            ) : (
              <>{displayState.headerText}</>
            )}
          </MainText>
          {(wod.type_id === 2 || wod.type_id === 5) && (
            <MainText
              fontSize={"0.8rem"}
              sx={{ color: Theme.palette.muted + " !important" }}
            >
              <TrackChangesIcon sx={{ fontSize: "0.8rem" }} /> Target:{" "}
              <span style={{ color: Theme.palette.orange }}>
                {displayState.projectedScoreValue}
              </span>
            </MainText>
          )}
        </Stack>
        <Box mt={3} textAlign={"center"}>
          {wod.structure_display && wod.type_id === 1 && (
            <MainText
              fontWeight={700}
              fontSize={mobileDevice || width < 800 ? "0.9rem" : "1.2rem"}
              sx={{ color: Theme.palette.orange + " !important" }}
            >
              {wod.structure}
            </MainText>
          )}
        </Box>
        {wod.type === 1 && wod.rounds > 1 && (
          <Box textAlign={"center"} mb={3}>
            <MainText
              fontWeight={700}
              fontSize={mobileDevice || width < 800 ? "0.9rem" : "1.2rem"}
              sx={{ color: Theme.palette.orange + " !important" }}
            >
              {wod.rounds} rounds of:
            </MainText>
          </Box>
        )}
        <Stack gap={2}>
          {displayState.baseExercises.map(
            ({ key, index, item, displayReps, showMinuteLabel, runLabel }) => (
              <Fade in={true} timeout={1000 * (index + 1)} key={key}>
                <SpacedRow gap={isCompact ? 1 : 3}>
                  <CenteredRow gap={isCompact ? 1 : 3}>
                    {showMinuteLabel && (
                      <Box
                        minWidth={
                          mobileDevice || isSmallScreen ? "40px" : "60px"
                        }
                      >
                        <MainText
                          fontSize={(mobileDevice || width < 800) && "0.8rem"}
                        >
                          Min {index + 1}:
                        </MainText>
                      </Box>
                    )}
                    {!wod.structure_display && (
                      <Box
                        width={mobileDevice || isSmallScreen ? "30px" : "40px"}
                      >
                        <MainText
                          fontWeight={700}
                          fontSize={
                            mobileDevice || width < 800 ? "0.9rem" : "1.2rem"
                          }
                          sx={{ color: Theme.palette.orange + " !important" }}
                        >
                          {displayReps}
                        </MainText>
                      </Box>
                    )}
                    <CenteredRow gap={1}>
                      <MainText
                        fontSize={
                          mobileDevice || width < 800 ? "0.8rem" : "1.2rem"
                        }
                      >
                        {item.unit !== "rep" && <>{item.unit}&nbsp;</>}
                        {item.exercise}
                      </MainText>
                      {runLabel && (
                        <MainText fontSize={"0.7rem"}>({runLabel})</MainText>
                      )}
                    </CenteredRow>
                  </CenteredRow>
                  {item.category_id >= 4 && (
                    <ExerciseWeight weight_info={item.weight_info} />
                  )}
                </SpacedRow>
              </Fade>
            ),
          )}
          {displayState.stepExercises.map(
            ({ key, index, item, displayReps, runLabel }) => (
              <Fade
                in={true}
                timeout={1000 * (index + 1 + displayState.baseExercises.length)}
                key={key}
              >
                <SpacedRow gap={isCompact ? 1 : 3}>
                  <CenteredRow gap={isCompact ? 1 : 3}>
                    <Box
                      width={mobileDevice || isSmallScreen ? "30px" : "40px"}
                    >
                      <MainText
                        fontWeight={700}
                        fontSize={
                          mobileDevice || width < 800 ? "0.9rem" : "1.2rem"
                        }
                        sx={{ color: Theme.palette.orange + " !important" }}
                      >
                        {displayReps}
                      </MainText>
                    </Box>
                    <CenteredRow gap={1}>
                      <MainText
                        fontSize={
                          mobileDevice || width < 800 ? "0.9rem" : "1.2rem"
                        }
                      >
                        {item.unit !== "rep" && <>{item.unit} </>}
                        {item.exercise}
                      </MainText>
                      {runLabel && <MainText>({runLabel})</MainText>}
                    </CenteredRow>
                  </CenteredRow>
                  {item.category_id >= 4 && (
                    <ExerciseWeight
                      weight_info={item.weight_info}
                      dark={false}
                    />
                  )}
                </SpacedRow>
              </Fade>
            ),
          )}
        </Stack>
      </Stack>
    </MainCard>
  );
};
export default memo(GenWodDisplay);
